// src/App.js
import { Suspense, lazy } from "react";
import { Route, Navigate, useParams } from "react-router-dom";

import Loader from "./components/Loader";
import Layout from "./layout/Layout";
import { permissionService } from "./services/permissionService";

function LegacySalonSetPasswordRedirect() {
  const { token } = useParams();
  return <Navigate to={`/nyafaz-salon/set-password/${token}`} replace />;
}

/** Portal emails mistakenly used FRONTEND_URL=/nyafaz-salon + /enterprise/... */
function MisroutedEnterpriseSetPasswordRedirect() {
  const { token } = useParams();
  return <Navigate to={`/enterprise/activate/set-password/${token}`} replace />;
}

const LoginSign = lazy(() => import("./pages/LoginSign"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Verify = lazy(() => import("./pages/Verify"));
const ProductList = lazy(() => import("./pages/ProductList"));
const Servicelist = lazy(() => import("./pages/Servicelist"));
const AddService = lazy(() => import("./pages/AddService"));
const Addproduct = lazy(() => import("./pages/Addproduct"));
const Setflow = lazy(() => import("./pages/Setflow"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const Permission = lazy(() => import("./pages/Permission"));
const AddPlan = lazy(() => import("./pages/AddPlan"));
const Integration = lazy(() => import("./pages/Integration"));
const IbDashboard = lazy(() => import("./pages/IbDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Contact = lazy(() => import("./pages/Contact"));
const AiAnalytics = lazy(() => import("./pages/AiAnalytics"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const AiMarketing = lazy(() => import("./pages/AiMarketing"));
const EmbedScript = lazy(() => import("./pages/EmbedScript"));
const UserList = lazy(() => import("./pages/UserList"));
const OrganizationList = lazy(() => import("./pages/OrganizationList"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const PaymentRequests = lazy(() => import("./pages/PaymentRequests"));
const SubscriptionDetail = lazy(() => import("./pages/SubscriptionDetail"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const PaymentDetail = lazy(() => import("./pages/PaymentDetail"));
const AdminContactMessages = lazy(() => import("./pages/AdminContactMessages"));
const AdminScanTopupPacks = lazy(() => import("./pages/AdminScanTopupPacks"));
const AdminPlanSalonLoginLimits = lazy(() => import("./pages/AdminPlanSalonLoginLimits"));
const AdminWhatsAppCredentials = lazy(() => import("./pages/AdminWhatsAppCredentials"));
const MakeupCatalog = lazy(() => import("./pages/MakeupCatalog"));
const WhatsAppSettings = lazy(() => import("./pages/WhatsAppSettings"));
const StylistManagement = lazy(() => import("./pages/StylistManagement"));
const RouteFallback = () => <Loader fullScreen />;

const withSuspense = (node) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const UserDashboardRoute = () => {
  const isAdmin = permissionService.isPlatformAdmin();
  return isAdmin ? <Navigate to="/nyafaz-salon/admin-dashboard" replace /> : withSuspense(<IbDashboard />);
};

const AdminDashboardRoute = () => {
  const isAdmin = permissionService.isPlatformAdmin();
  return isAdmin ? withSuspense(<AdminDashboard />) : <Navigate to="/nyafaz-salon/ib-dashboard" replace />;
};

const AdminOnlyRoute = ({ children }) => {
  const isAdmin = permissionService.isPlatformAdmin();
  return isAdmin ? children : <Navigate to="/nyafaz-salon/ib-dashboard" replace />;
};

export function renderSalonAdminRoutes({ salonAdminHome = false } = {}) {
  return (
    <>
      {salonAdminHome ? <Route path="/" element={<Navigate to="/nyafaz-salon" replace />} /> : null}

      {/* Redirect old routes */}
        <Route path="/productlist" element={<Navigate to="/nyafaz-salon/productlist" replace />} />
        <Route path="/add-product" element={<Navigate to="/nyafaz-salon/add-product" replace />} />
        <Route path="/edit-product" element={<Navigate to="/nyafaz-salon/edit-product" replace />} />
        <Route path="/view-product" element={<Navigate to="/nyafaz-salon/view-product" replace />} />
        <Route path="/overview" element={<Navigate to="/nyafaz-salon/overview" replace />} />
        <Route path="/ib-dashboard" element={<Navigate to="/nyafaz-salon/ib-dashboard" replace />} />
        {/* <Route path="/verify/:token" element={<Navigate to="/nyafaz-salon/verify/:token" replace />} /> */}
        {/* <Route path="/verify" element={<Navigate to="/nyafaz-salon/verify" replace />} /> */}
        <Route path="/brandflow" element={<Navigate to="/nyafaz-salon/salonflow" replace />} />
        <Route path="/salonflow" element={<Navigate to="/nyafaz-salon/salonflow" replace />} />
        <Route path="/edit-profile" element={<Navigate to="/nyafaz-salon/edit-profile" replace />} />
        <Route path="/plan-details" element={<Navigate to="/nyafaz-salon/plan-details" replace />} />
        <Route path="/integration" element={<Navigate to="/nyafaz-salon/integration" replace />} />
        <Route path="/embed-script" element={<Navigate to="/nyafaz-salon/embed-script" replace />} />
        <Route path="/contact-us" element={<Navigate to="/nyafaz-salon/contact-us" replace />} />
        <Route path="/organization-permission" element={<Navigate to="/nyafaz-salon/organization-permission" replace />} />
        <Route path="/add-plan" element={<Navigate to="/nyafaz-salon/add-plan" replace />} />
        <Route path="/organization" element={<Navigate to="/nyafaz-salon/organization" replace />} />
        <Route path="/brand-users" element={<Navigate to="/nyafaz-salon/brand-users" replace />} />
        <Route path="/invoices" element={<Navigate to="/nyafaz-salon/invoices" replace />} />
        <Route path="/subscriptions" element={<Navigate to="/nyafaz-salon/subscriptions" replace />} />
        <Route path="/payment-requests" element={<Navigate to="/nyafaz-salon/payment-requests" replace />} />
        <Route path="/admin-contact-messages" element={<Navigate to="/nyafaz-salon/admin-contact-messages" replace />} />
        <Route path="/admin-scan-topup-packs" element={<Navigate to="/nyafaz-salon/admin-scan-topup-packs" replace />} />
        <Route path="/admin-plan-salon-login-limits" element={<Navigate to="/nyafaz-salon/admin-plan-salon-login-limits" replace />} />
        <Route path="/admin-whatsapp-credentials" element={<Navigate to="/nyafaz-salon/admin-whatsapp-credentials" replace />} />
        {/* Pages without layout */}
        <Route path="/nyafaz-salon" element={withSuspense(<LoginSign />)} />
        <Route path="/nyafaz-sales/*" element={<Navigate to="/nyafaz-salon" replace />} />
        <Route
          path="/nyafaz-salon/nyafaz-salon/set-password/:token"
          element={<LegacySalonSetPasswordRedirect />}
        />
        <Route
          path="/nyafaz-salon/enterprise/salon-admin/set-password/:token"
          element={<MisroutedEnterpriseSetPasswordRedirect />}
        />
        <Route
          path="/nyafaz-salon/set-password/:token"
          element={withSuspense(<SetPassword />)}
        />
        <Route path="/nyafaz-salon/verify/:token" element={withSuspense(<Verify />)} />
        <Route path="/verify" element={withSuspense(<Verify />)} />

        {/* Pages with Layout */}
        <Route
          path="/nyafaz-salon/overview"
          element={
            <Layout hideSidebar={true} dashboardOnly={true}>
              <Suspense fallback={<RouteFallback />}>
                <Dashboard />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/ib-dashboard"
          element={
            <Layout>
              <UserDashboardRoute />
            </Layout>

          }
        />
        <Route
          path="/nyafaz-salon/admin-dashboard"
          element={
            <Layout>
              <AdminDashboardRoute />
            </Layout>

          }
        />
        <Route
          path="/nyafaz-salon/productlist"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <ProductList />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/servicelist"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Servicelist />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/add-service"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AddService />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/edit-service"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AddService />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/view-service"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AddService />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/add-product"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Addproduct />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/edit-product"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Addproduct />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/view-product"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Addproduct />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/salonflow"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Setflow />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/integration"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Integration />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/embed-script"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <EmbedScript />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/contact-us"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Contact />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/whatsapp-settings"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <WhatsAppSettings />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/stylists"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <StylistManagement />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/ai-analytics"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AiAnalytics />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/my-orders"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <MyOrders />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/ai-campaign"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AiMarketing />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/brand-users"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <UserList />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/organization"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <OrganizationList />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/edit-profile"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <EditProfile />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/plan-details"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Settings />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/organization-permission"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Permission />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/add-plan"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AddPlan />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/invoices"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Invoices />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/subscriptions"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <Subscriptions />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/payment-requests"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <PaymentRequests />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/admin-contact-messages"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <AdminContactMessages />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/admin-scan-topup-packs"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<AdminScanTopupPacks />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-salon/admin-plan-salon-login-limits"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<AdminPlanSalonLoginLimits />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-salon/makeup-catalog"
          element={
            <Layout makeupOnly={true}>
              {withSuspense(<MakeupCatalog variant="org" />)}
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/admin-makeup-colors"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<MakeupCatalog variant="admin-colors" />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-salon/admin-makeup-finishes"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<MakeupCatalog variant="admin-finishes" />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-salon/admin-makeup-templates"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<MakeupCatalog variant="admin-templates" />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route
          path="/nyafaz-salon/admin-whatsapp-credentials"
          element={
            <AdminOnlyRoute>
              <Layout>{withSuspense(<AdminWhatsAppCredentials />)}</Layout>
            </AdminOnlyRoute>
          }
        />

        <Route path="/nyafaz-salon/sales-team/*" element={<Navigate to="/nyafaz-salon/admin-dashboard" replace />} />

        <Route
          path="/nyafaz-salon/subscriptions/:id"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <SubscriptionDetail />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/nyafaz-salon/invoice/:id"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <InvoiceDetail />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/subscription-detail"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <SubscriptionDetail />
              </Suspense>
            </Layout>
          }
        />
        <Route
          path="/nyafaz-salon/payments/:id"
          element={
            <Layout>
              <Suspense fallback={<RouteFallback />}>
                <PaymentDetail />
              </Suspense>
            </Layout>
          }
        />
    </>
  );
}
